<!-- src/views/admin/Firmwares.vue -->
<template>
  <admin-layout title="Firmware OTA">
    <!-- Bandeau parc -->
    <div class="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
      <div class="bg-white rounded-lg shadow p-4">
        <p class="text-xs text-gray-500 uppercase tracking-wide">Version dispo la + récente</p>
        <p class="mt-1 text-2xl font-semibold text-primary-600">{{ latestLabel || '—' }}</p>
      </div>
      <div class="bg-white rounded-lg shadow p-4">
        <p class="text-xs text-gray-500 uppercase tracking-wide">À jour</p>
        <p class="mt-1 text-2xl font-semibold text-green-600">{{ parc.upToDate }}</p>
      </div>
      <div class="bg-white rounded-lg shadow p-4">
        <p class="text-xs text-gray-500 uppercase tracking-wide">À mettre à jour</p>
        <p class="mt-1 text-2xl font-semibold text-amber-600">{{ parc.outdated }}</p>
      </div>
      <div class="bg-white rounded-lg shadow p-4">
        <p class="text-xs text-gray-500 uppercase tracking-wide">Pré-versionnement</p>
        <p class="mt-1 text-2xl font-semibold text-gray-500">{{ parc.preVersion }}</p>
      </div>
    </div>

    <!-- Déploiement par vagues (06/10/2026) -->
    <ota-deploiements :versions="deployableVersions.filter(v => !v.banc)" :devices="devices" @change="fetchAll" />

    <!-- Parc & mise à jour OTA (canari = sélection manuelle device par device) -->
    <div class="bg-white rounded-lg shadow overflow-hidden mb-6">
      <div class="px-5 py-3 border-b border-gray-200">
        <h2 class="text-lg font-medium text-gray-900">Parc &amp; mise à jour OTA</h2>
        <p class="text-sm text-gray-500">Une carte à la fois (dépannage, essai). Toute version dont les <strong>2 slots (A&nbsp;et&nbsp;B)</strong> sont uploadés est proposée — montée <em>ou</em> retour à une version antérieure (downgrade). L'installation n'a jamais lieu système armé, sauf urgence.</p>
      </div>
      <table class="min-w-full divide-y divide-gray-200 text-sm">
        <thead class="bg-gray-50">
          <tr class="text-left text-gray-500">
            <th class="px-4 py-2 font-medium">Device</th>
            <th class="px-4 py-2 font-medium">Firmware</th>
            <th class="px-4 py-2 font-medium">Slot actif</th>
            <th class="px-4 py-2 font-medium">Campagne</th>
            <th class="px-4 py-2 font-medium">Mise à jour</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr v-for="d in devices" :key="d.id">
            <td class="px-4 py-2">
              <div class="font-medium text-gray-900">{{ d.name || 'Sans nom' }}</div>
              <div class="text-xs text-gray-400">{{ d.client_name }} · {{ d.ident }}</div>
            </td>
            <td class="px-4 py-2">
              <span v-if="d.fw_ver != null" class="font-medium text-gray-700">{{ fwLabel(d.fw_ver) }}</span>
              <span v-else class="text-gray-400">Pré-versionnement</span>
            </td>
            <td class="px-4 py-2 text-gray-600">{{ d.active_slot || '—' }}</td>
            <td class="px-4 py-2">
              <span v-if="campaignFor(d.id)" :class="statusClass(campaignFor(d.id).status)"
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium">
                {{ statusLabel(campaignFor(d.id)) }}
              </span>
              <span v-else class="text-gray-300">—</span>
              <div v-if="campaignFor(d.id) && campaignFor(d.id).last_error" class="text-xs text-red-500 mt-0.5">
                {{ campaignFor(d.id).last_error }}
              </div>
              <div v-if="campActive(d.id) && campActive(d.id).imposee" class="text-xs text-red-700 mt-0.5">imposée par le support</div>
              <div v-if="campActive(d.id) && campActive(d.id).attente_desarmement" class="text-xs text-amber-700 mt-0.5">attend le désarmement</div>
              <div v-if="campActive(d.id) && campActive(d.id).rollout_id" class="text-xs text-gray-400 mt-0.5">déploiement #{{ campActive(d.id).rollout_id }}</div>
            </td>
            <td class="px-4 py-2">
              <div v-if="campActive(d.id)" class="flex items-center gap-2 flex-wrap">
                <span v-if="campActive(d.id).status === 'pending'" class="text-xs text-gray-500">⤓ {{ campActive(d.id).target_label }} (téléchargement)</span>
                <span v-else class="text-xs text-primary-600 font-medium">{{ campActive(d.id).target_label }} prête</span>
                <!-- Imposer (06/10/2026) : possible dès le téléchargement ; confirmation si le client
                     est en manuel ; tracé dans l'historique, titulaire prévenu à l'installation. -->
                <button v-if="!campActive(d.id).install_requested && !campActive(d.id).imposee" @click="installNow(d, campActive(d.id))"
                        class="px-2.5 py-1 rounded bg-primary-600 text-white text-xs font-medium hover:bg-primary-700">
                  {{ campActive(d.id).client_manuel ? 'Imposer l’installation' : 'Installer' }}
                </button>
                <span v-else-if="campActive(d.id).status === 'ready'" class="text-xs text-gray-400">installation demandée…</span>
                <button @click="cancel(d)" class="text-red-600 hover:text-red-800 text-xs font-medium">Annuler</button>
              </div>
              <div v-else class="flex items-center gap-2">
                <select v-model="targetSel[d.id]"
                        class="rounded border-gray-300 shadow-sm text-xs focus:ring-primary-500 focus:border-primary-500 py-1">
                  <option value="">Choisir…</option>
                  <option v-for="v in targetsFor(d)" :key="v.version" :value="v.version">{{ v.label }}</option>
                </select>
                <button @click="launch(d)" :disabled="!targetSel[d.id]"
                        class="px-2.5 py-1 rounded bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 disabled:opacity-40">
                  Lancer
                </button>
                <span v-if="targetsFor(d).length === 0" class="text-xs text-gray-400">
                  Aucune version éligible (2 slots A+B requis)
                </span>
              </div>
            </td>
          </tr>
          <tr v-if="devices.length === 0">
            <td colspan="5" class="px-4 py-6 text-center text-gray-500">Aucun device.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Formulaire d'upload -->
    <div class="bg-white rounded-lg shadow p-5 mb-6">
      <h2 class="text-lg font-medium text-gray-900 mb-1">Uploader une release</h2>
      <p class="text-sm text-gray-500 mb-4">
        Dépose les <strong>2 binaires signés (slot A et B)</strong> <em>et</em> le
        <strong>manifeste <code>manifest_v*.json</code></strong> produit par le build (obligatoire).
        Le serveur vérifie l'intégrité (sha256 + CRC32 vs manifeste), enregistre la traçabilité
        (commit, toolchain) et <strong>refuse toute release <code>git_dirty</code></strong>.
      </p>

      <form @submit.prevent="submit" class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Fichiers de release (2 .bin + manifeste .json)</label>
          <input type="file" accept=".bin,.json" multiple ref="fileInput" @change="onFile"
                 class="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100" />
          <p v-if="files.length" class="mt-1 text-xs text-gray-500">{{ files.length }} fichier(s) sélectionné(s) : {{ fileNames }}</p>
          <p v-if="files.length && !hasManifest" class="mt-1 text-xs text-amber-600">⚠️ Aucun fichier .json détecté — le manifeste est obligatoire.</p>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Notes <span class="text-gray-400">(optionnel)</span></label>
          <input v-model="form.notes" type="text" placeholder="release notes courtes"
                 class="block w-full sm:w-1/2 rounded border-gray-300 shadow-sm text-sm focus:ring-primary-500 focus:border-primary-500" />
        </div>

        <div v-if="error" class="text-sm text-red-600 whitespace-pre-line">{{ error }}</div>
        <div v-if="success" class="text-sm text-green-600 whitespace-pre-line">{{ success }}</div>

        <button type="submit" :disabled="!files.length || uploading"
                class="px-4 py-2 rounded bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50">
          {{ uploading ? 'Upload…' : 'Uploader' }}
        </button>
      </form>
    </div>

    <!-- Inventaire -->
    <div class="bg-white rounded-lg shadow overflow-hidden">
      <div class="px-5 py-3 border-b border-gray-200">
        <h2 class="text-lg font-medium text-gray-900">Inventaire ({{ firmwares.length }})</h2>
      </div>

      <div v-if="loading" class="p-6 text-center text-gray-500">Chargement…</div>
      <div v-else-if="firmwares.length === 0" class="p-6 text-center text-gray-500">
        Aucun firmware uploadé.
      </div>
      <table v-else class="min-w-full divide-y divide-gray-200 text-sm">
        <thead class="bg-gray-50">
          <tr class="text-left text-gray-500">
            <th class="px-4 py-2 font-medium">Version</th>
            <th class="px-4 py-2 font-medium">Slot</th>
            <th class="px-4 py-2 font-medium">Taille</th>
            <th class="px-4 py-2 font-medium">CRC32</th>
            <th class="px-4 py-2 font-medium">Build</th>
            <th class="px-4 py-2 font-medium">Uploadé</th>
            <th class="px-4 py-2 font-medium">URL download</th>
            <th class="px-4 py-2"></th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr v-for="f in firmwares" :key="f.id">
            <td class="px-4 py-2 font-medium text-gray-900">{{ f.version_label }}
              <span v-if="f.banc" class="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">banc</span>
              <span v-if="f.archivee" class="ml-1 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">archivée</span>
            </td>
            <td class="px-4 py-2">
              <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">{{ f.slot }}</span>
            </td>
            <td class="px-4 py-2 text-gray-600">{{ humanSize(f.size) }}</td>
            <td class="px-4 py-2 text-gray-500 font-mono text-xs">{{ f.crc32 || '—' }}</td>
            <td class="px-4 py-2 text-gray-500 text-xs">
              <span v-if="f.git_short" class="font-mono" :title="`${f.toolchain || ''}${f.built_at ? ' — ' + formatDate(f.built_at) : ''}`">{{ f.git_short }}</span>
              <span v-else class="text-gray-300">—</span>
            </td>
            <td class="px-4 py-2 text-gray-600">{{ formatDate(f.uploaded_at) }}</td>
            <td class="px-4 py-2">
              <span v-if="f.archivee" class="text-xs text-gray-400">hors ligne (archivée)</span>
              <a v-else :href="f.download_url" class="text-primary-600 hover:underline text-xs break-all">{{ f.download_url }}</a>
            </td>
            <td class="px-4 py-2 text-right">
              <div v-if="f.slot === 'A'" class="flex justify-end gap-3 text-xs font-medium whitespace-nowrap">
                <button @click="basculerBanc(f)" class="text-amber-700 hover:text-amber-900">{{ f.banc ? 'Proposable au parc' : 'Build de banc' }}</button>
                <button @click="archiver(f, !f.archivee)" class="text-gray-700 hover:text-gray-900">{{ f.archivee ? 'Remettre en ligne' : 'Archiver' }}</button>
                <button @click="remove(f)" class="text-red-600 hover:text-red-800">Supprimer</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </admin-layout>
</template>

<script>
import { FUSEAU } from '@/i18n/dates';
import { defineComponent, ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import AdminLayout from '@/components/layout/AdminLayout.vue';
import apiService from '@/services/api.service';
import socketService from '@/services/socket.service';
import { formatVersion } from '@/utils/fwVersion';
import OtaDeploiements from '@/components/admin/OtaDeploiements.vue';

export default defineComponent({
  name: 'AdminFirmwares',
  components: { AdminLayout, OtaDeploiements },

  setup() {
    const firmwares = ref([]);
    const latestVersion = ref(null);
    const latestLabel = ref(null);
    const devices = ref([]);
    const campaigns = ref([]);
    const loading = ref(true);
    const uploading = ref(false);
    const error = ref('');
    const success = ref('');
    const files = ref([]);
    const fileInput = ref(null);
    const form = reactive({ notes: '' });
    const targetSel = reactive({});

    const fileNames = computed(() => files.value.map(f => f.name).join(', '));
    const hasManifest = computed(() => files.value.some(f => /\.json$/i.test(f.name)));

    const parc = computed(() => {
      const out = { upToDate: 0, outdated: 0, preVersion: 0 };
      for (const d of devices.value) {
        if (d.fw_ver === null || d.fw_ver === undefined) { out.preVersion++; continue; }
        if (latestVersion.value !== null && d.fw_ver >= latestVersion.value) out.upToDate++;
        else out.outdated++;
      }
      return out;
    });

    // Versions déployables = celles ayant les 2 slots A et B uploadés
    const deployableVersions = computed(() => {
      const bySlot = {};
      // Archivées : plus téléchargeables, donc jamais proposées (06/10/2026). `banc` suit la version.
      for (const f of firmwares.value.filter(x => !x.archivee)) {
        bySlot[f.version] = bySlot[f.version] || { version: f.version, label: f.version_label + (f.banc ? ' (banc)' : ''), banc: !!f.banc, slots: new Set() };
        bySlot[f.version].slots.add(f.slot);
      }
      return Object.values(bySlot)
        .filter(v => v.slots.has('A') && v.slots.has('B'))
        .sort((a, b) => b.version - a.version);
    });

    // Toute version déployable (2 slots), sauf la version exacte déjà installée (no-op).
    // Downgrade autorisé → on ne filtre plus sur « > installée ».
    const targetsFor = (d) =>
      deployableVersions.value.filter(v => d.fw_ver == null || v.version !== d.fw_ver);

    const campaignFor = (deviceId) =>
      campaigns.value.find(c => c.device_id === deviceId && (c.status === 'pending'))
      || campaigns.value.filter(c => c.device_id === deviceId).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0];

    const activePending = (deviceId) => {
      const c = campaignFor(deviceId);
      return c && c.status === 'pending';
    };

    // Campagne non terminale (pending = download, ready = stagée en attente d'install)
    const campActive = (deviceId) =>
      campaigns.value.find(c => c.device_id === deviceId && (c.status === 'pending' || c.status === 'ready'));

    const fetchAll = async () => {
      try {
        const [fwRes, devRes, campRes] = await Promise.all([
          apiService.getFirmwares(),
          apiService.getAllDevices(),
          apiService.getOtaCampaigns()
        ]);
        firmwares.value = fwRes.data.firmwares || [];
        latestVersion.value = fwRes.data.latest_version;
        latestLabel.value = fwRes.data.latest_label;
        devices.value = devRes.data || [];
        campaigns.value = campRes.data.campaigns || [];
      } catch (e) {
        console.error('Erreur chargement OTA:', e);
      } finally {
        loading.value = false;
      }
    };

    const onFile = (e) => { files.value = Array.from(e.target.files || []); error.value = ''; success.value = ''; };

    const submit = async () => {
      if (!files.value.length) return;
      uploading.value = true; error.value = ''; success.value = '';
      try {
        const fd = new FormData();
        files.value.forEach(f => fd.append('files', f));
        if (form.notes) fd.append('notes', form.notes);
        const { data } = await apiService.uploadFirmware(fd);
        const ok = (data.uploaded || []).map(f => `${f.version_label} slot ${f.slot}`);
        success.value = ok.length ? `Release ajoutée : ${ok.join(', ')} (intégrité vérifiée).` : '';
        error.value = '';
        form.notes = '';
        files.value = [];
        if (fileInput.value) fileInput.value.value = '';
        await fetchAll();
      } catch (e) {
        // Rejet d'ingestion (§5) : message global + détail par slot si fourni.
        const d = e.response?.data || {};
        const detail = (d.errors || []).map(x => `• ${x.slot ? `slot ${x.slot} : ` : ''}${x.error}`);
        error.value = [d.message || 'Échec de l\'upload', ...detail].join('\n');
      } finally {
        uploading.value = false;
      }
    };

    const remove = async (f) => {
      if (!confirm(`Supprimer le firmware ${f.version_label} slot ${f.slot} ?`)) return;
      try { await apiService.deleteFirmware(f.id); await fetchAll(); }
      catch (e) { alert(e.response?.data?.message || 'Échec de la suppression'); }
    };

    const launch = async (d, force = false) => {
      const target = targetSel[d.id];
      if (!target) return;
      try {
        await apiService.createOtaCampaign(d.id, target, force);
        targetSel[d.id] = '';
        await fetchAll();
      } catch (e) {
        const data = e.response?.data || {};
        // §5 : filet batterie serveur. Forçable → on confirme avec l'admin puis on relance en force.
        if (data.code === 'low_battery' && data.overridable && !force) {
          if (confirm(`${data.message}\n\nLancer quand même la mise à jour ?`)) {
            return launch(d, true);
          }
          return;
        }
        alert(data.message || 'Échec du lancement de campagne');
      }
    };

    const archiver = async (f, oui) => {
      if (!confirm(oui ? `Archiver ${f.version_label} ? Ses binaires ne seront plus téléchargeables (ni proposés).`
                       : `Remettre ${f.version_label} en ligne ? Ses binaires redeviennent téléchargeables.`)) return;
      try { await apiService.archiverFirmware(f.version, oui); await fetchAll(); }
      catch (e) { alert(e.response?.data?.message || 'Opération impossible'); }
    };
    const basculerBanc = async (f) => {
      try { await apiService.marquerBanc(f.version, !f.banc); await fetchAll(); }
      catch (e) { alert(e.response?.data?.message || 'Opération impossible'); }
    };

    const cancel = async (d) => {
      if (!confirm(`Annuler la campagne OTA de ${d.name} ?`)) return;
      try { await apiService.cancelOtaCampaign(d.id); await fetchAll(); }
      catch (e) { alert(e.response?.data?.message || 'Échec de l\'annulation'); }
    };

    // Installation imposée (06/10/2026). Le serveur exige une confirmation si le client a choisi
    // la mise à jour manuelle ; système armé, elle attend le désarmement, sauf urgence.
    const installNow = async (d, camp) => {
      const arme = d.state === 'armed';
      let urgence = false;
      if (camp && camp.client_manuel) {
        if (!confirm(`${d.client_name || 'Ce client'} a choisi la mise à jour MANUELLE.\n\nImposer l'installation de ${camp.target_label} sur ${d.name} ?`
          + `\nLa carte va redémarrer quelques instants. L'intervention sera visible dans son historique, et le titulaire sera prévenu une fois la carte mise à jour.`)) return;
      }
      if (arme) {
        urgence = confirm(`${d.name} est ARMÉE : l'installation attendra normalement le désarmement.\n\nURGENCE : installer quand même, système armé ? `
          + `La carte ne protégera plus pendant le redémarrage.\n\n(Annuler = attendre le désarmement)`);
      }
      try {
        const { data } = await apiService.installOtaCampaign(d.id, { confirme: true, urgence });
        if (data && data.attente) alert(data.message);
        await fetchAll();
      } catch (e) { alert(e.response?.data?.message || 'Échec du lancement de l\'installation'); }
    };

    const fwLabel = (v) => formatVersion(v);
    const humanSize = (n) => n == null ? '—' : (n < 1024 ? `${n} o` : `${(n / 1024).toFixed(1)} ko`);
    const formatDate = (iso) => iso ? new Date(iso).toLocaleString('fr-FR', { timeZone: FUSEAU, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

    const statusClass = (s) => ({
      pending: 'bg-amber-100 text-amber-800',
      ready: 'bg-indigo-100 text-indigo-800',
      ok: 'bg-green-100 text-green-800',
      stopped: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-600'
    }[s] || 'bg-gray-100 text-gray-600');

    const statusLabel = (c) => {
      if (!c) return '';
      if (c.status === 'pending') {
        let txt = `Téléchargement → ${c.target_label} (essai ${c.attempts}/${c.max_attempts})`;
        if (c.progress_pct != null) txt += ` ${c.progress_pct}%`;
        return txt;
      }
      if (c.status === 'ready') return c.install_requested ? `Installation… → ${c.target_label}` : `Prête à installer → ${c.target_label}`;
      if (c.status === 'ok') return `À jour ${c.target_label}`;
      if (c.status === 'stopped') return `Stoppée (${c.last_error || 'échec'})`;
      if (c.status === 'cancelled') return 'Annulée';
      return c.status;
    };

    let socket = null;
    const onOtaUpdate = () => fetchAll();

    onMounted(() => {
      fetchAll();
      try { socket = socketService.getSocket(); socket.on('ota:update', onOtaUpdate); } catch (e) { /* ws optionnel */ }
    });
    onUnmounted(() => { if (socket) socket.off('ota:update', onOtaUpdate); });

    return {
      firmwares, latestLabel, parc, devices, campaigns, loading, uploading, error, success,
      files, fileNames, hasManifest, fileInput, form, targetSel,
      onFile, submit, remove, launch, cancel, installNow, fetchAll, deployableVersions, archiver, basculerBanc,
      targetsFor, campaignFor, activePending, campActive,
      fwLabel, humanSize, formatDate, statusClass, statusLabel
    };
  }
});
</script>
